import {isFeatureEnabled} from '@github-ui/feature-flags'
import {KebabHorizontalIcon, LinkIcon, PaperclipIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Truncate} from '@primer/react'
import {clsx} from 'clsx'
import type React from 'react'
import {useCallback, useMemo, useRef, useState} from 'react'

import {referenceID, referenceName, referenceURL, validReferenceURL} from '../utils/copilot-chat-helpers'
import type {CopilotChatReference} from '../utils/copilot-chat-types'
import {getSubsetFromState, useChatStateLens} from '../utils/CopilotChatContext'
import {useChatManager} from '../utils/CopilotChatManagerContext'
import styles from './ChatReference.module.css'
import {
  ActionMenuOverlay,
  COPILOT_CHAT_MENU_PORTAL_ROOT,
  COPILOT_CHAT_MESSAGES_MENU_PORTAL_ROOT,
} from './PortalContainerUtils'
import {iconForReference} from './ReferenceToken'

export interface ChatReferenceProps {
  reference: CopilotChatReference
  onClick?: (reference: CopilotChatReference, event?: React.MouseEvent<HTMLAnchorElement>) => void
}

export function OutputReference({reference, onClick}: ChatReferenceProps) {
  // When the flag is on, read only `mode` and `currentReferences`. Those don't change while tokens
  // stream, so reference rows in a completed message won't re-render on every token. When the flag is
  // off, read the whole state, which keeps the old behavior.
  const memoizeEnabled = isFeatureEnabled('copilot_dotcom_chat_memoize_messages')
  const {mode = 'assistive', currentReferences = []} = useChatStateLens(s =>
    memoizeEnabled ? getSubsetFromState(s, ['mode', 'currentReferences']) : s,
  )
  const manager = useChatManager()
  const actionButtonRef = useRef<HTMLButtonElement | null>(null)
  const [moreMenuOpen, setMoreMenuOpen] = useState(false)

  const selectedRefIDs = useMemo(() => currentReferences.map(r => referenceID(r)), [currentReferences])

  const thisRefID = useMemo(() => referenceID(reference), [reference])
  const isRepoInstructions = useMemo(() => reference.type === 'repo-instructions', [reference])
  const isOrgInstructions = useMemo(() => reference.type === 'org-instructions', [reference])

  const showTrailingVisual = !isRepoInstructions && !isOrgInstructions && reference.type !== 'web-search-result'
  const isInCurrentReferences = useMemo(
    () => isRepoInstructions || isOrgInstructions || selectedRefIDs.includes(thisRefID),

    [isRepoInstructions, isOrgInstructions, selectedRefIDs, thisRefID],
  )
  const handleAddReference = useCallback(
    (e: React.SyntheticEvent) => {
      e.stopPropagation()
      if (isInCurrentReferences) return
      manager.addReference(reference, 'refMenu')
    },
    [manager, reference, isInCurrentReferences],
  )

  const refURL = referenceURL(reference)
  const validURL = validReferenceURL(refURL)
  const Icon = iconForReference(reference)

  let filePath
  switch (reference.type) {
    case 'file':
    case 'snippet': {
      filePath = `${reference.repoName}`

      const index = reference.path.lastIndexOf('/')
      if (index !== -1) {
        filePath = `${filePath}/${reference.path.substring(0, index)}`
      }
      break
    }
    case 'web-search-result':
      filePath = new URL(reference.url, window.location.origin).hostname.replace(/^www\./, '')
      break
  }

  const referenceTitle = referenceName(reference)

  return (
    <ActionList.LinkItem
      onClick={e => {
        if (onClick && !e.defaultPrevented) {
          onClick(reference, e)
        }
      }}
      href={refURL}
      rel="noopener"
      className={clsx('reference-action', styles.ActionList_LinkItem)}
      target={mode === 'immersive' || reference.type === 'third-party' ? '_blank' : undefined}
    >
      {Icon && (
        <ActionList.LeadingVisual>
          {/* eslint-disable-next-line react-hooks/static-components */}
          <Icon />
        </ActionList.LeadingVisual>
      )}

      <Truncate title={referenceTitle} maxWidth="100%" className="text-semibold">
        {referenceTitle}
        {filePath && <span className="color-fg-muted ml-2">{filePath}</span>}
      </Truncate>
      {showTrailingVisual && (
        <ActionList.TrailingAction
          onClick={() => setMoreMenuOpen(true)}
          icon={KebabHorizontalIcon}
          label="More reference options"
          ref={actionButtonRef}
        />
      )}
      {showTrailingVisual && (
        <ActionMenu anchorRef={actionButtonRef} open={moreMenuOpen} onOpenChange={setMoreMenuOpen}>
          <ActionMenuOverlay
            portalContainerName={
              mode === 'assistive' ? COPILOT_CHAT_MENU_PORTAL_ROOT : COPILOT_CHAT_MESSAGES_MENU_PORTAL_ROOT
            }
            position={mode === 'assistive' ? 'relative' : undefined}
            className={styles.referenceMenuOverlay}
          >
            <ActionList>
              {validURL &&
                (mode === 'assistive' ||
                mode === 'immersive' ||
                ['third-party', 'folder', 'repository'].includes(reference.type) ? (
                  <ActionList.LinkItem href={refURL} rel="noopener" target="_blank">
                    <ActionList.LeadingVisual>
                      <LinkIcon />
                    </ActionList.LeadingVisual>
                    Open
                  </ActionList.LinkItem>
                ) : (
                  <ActionList.Item
                    onSelect={() => {
                      manager.selectReference(reference)
                    }}
                  >
                    <ActionList.LeadingVisual>
                      <LinkIcon />
                    </ActionList.LeadingVisual>
                    Preview
                  </ActionList.Item>
                ))}
              <ActionList.Item onSelect={handleAddReference} disabled={isInCurrentReferences}>
                <ActionList.LeadingVisual>
                  <PaperclipIcon />
                </ActionList.LeadingVisual>
                Attach to chat
              </ActionList.Item>
            </ActionList>
          </ActionMenuOverlay>
        </ActionMenu>
      )}
    </ActionList.LinkItem>
  )
}
